import React from 'react'
import {Typography} from '@mui/material'
import moment from 'moment'
import {ColumnDef} from 'src/components-new/data/DataTable'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {EXAM_TYPE_OPTIONS} from './EchocardiographicRequestDialogNew'

export interface EchoRequestRow {
  _id: string
  createdAt?: string
  title?: string
  examType?: string
  patient?: string
  patientId?: string
  examId?: string
  state?: string
  description?: string
  [key: string]: any
}

interface EchoRequestColumnConfig {
  id: string
  label: string
  type?: string
  format?: string
}

// Same value/label pairs as the legacy page (src/pages/EchocardiographicRequests/EchocardiographicRequests.tsx) —
// kept here (rather than PSP-driven) since the backend status enum is fixed and both stacks
// must show identical Italian labels.
export const STATE_OPTIONS: {value: string; label: string}[] = [
  {value: 'TO_BE_COMPLETED', label: 'Da completare'},
  {value: 'COMPLETED', label: 'Completato'},
  {value: 'CANCELLED', label: 'Annullato'},
]

// `createdAt` intentionally appears twice (date column + time column), mirroring the
// legacy DEFAULT_COLUMNS — see EchocardiographicRequests.tsx lines 24-36. Both share the
// same `id` and `type: 'date'`; only `format` tells them apart. Real PSP-driven
// columnOptions configs may not (yet) follow this shape — see renderCell's fallback.
const DEFAULT_COLUMNS: EchoRequestColumnConfig[] = [
  {id: 'createdAt', label: 'DATA RICHIESTA', type: 'date', format: 'ddd DD MMM YYYY'},
  {id: 'createdAt', label: 'ORARIO', type: 'date', format: 'HH:mm'},
  {id: 'title', label: 'TITOLO'},
  {id: 'examType', label: 'TIPO ESAME'},
  {id: 'patient', label: 'PAZIENTE DI RIFERIMENTO'},
  {id: 'state', label: 'STATO REFERTAZIONE'},
]

function getExamTypeLabel(examType: string | undefined): string {
  if (!examType) return '—'
  return EXAM_TYPE_OPTIONS.find(opt => opt.value === examType)?.label ?? examType
}

// Falls back to the raw value (like the legacy switch's `default` branch) so an
// already-readable state coming from the backend/mock is not swallowed into '—'.
export function getStateLabel(state: string | undefined): string {
  return STATE_OPTIONS.find(opt => opt.value === state)?.label ?? (state ?? '—')
}

function getStateColor(state: string | undefined): 'success' | 'warning' | 'error' | 'default' {
  switch (state) {
    case 'COMPLETED':
      return 'success'
    case 'TO_BE_COMPLETED':
      return 'warning'
    case 'CANCELLED':
      return 'error'
    default:
      return 'default'
  }
}

/**
 * Some PSP configs list the "ORARIO" column with a mismatched `id` that doesn't
 * exist on the row (instead of the expected `id: 'createdAt'` + `type: 'date'`),
 * or predate the `type`/`format` fields entirely. Detect it via `id`/label so it
 * still derives its value from `createdAt` instead of always rendering '—' —
 * mirrors the same workaround in virtualMeetingsColumns.tsx.
 */
function looksLikeTimeColumn(label: string): boolean {
  const normalized = label.trim().toLowerCase()
  return normalized === 'orario' || normalized === 'ora'
}

/**
 * Driven by the PSP-configured `type`/`format` when present — the same
 * `id: 'createdAt'` is shared by the date column and the time column, and
 * `format` tells them apart (e.g. 'ddd DD MMM YYYY' vs 'HH:mm'), see
 * DEFAULT_COLUMNS. Older/real-world PSP configs may omit `type` and instead
 * use a mismatched `id` (e.g. `createdAtHours`, which the backend never
 * sends) or just the "ORARIO" label — `id`/`label` is checked as a fallback,
 * independently of whatever label happens to be configured (e.g. "Orario
 * esame" instead of "ORARIO"), so those still resolve from `createdAt`.
 */
function renderCell(id: string, type: string | undefined, label: string, format?: string) {
  return (row: EchoRequestRow): React.ReactNode => {
    const isTimeColumn = id === 'createdAtHours' || looksLikeTimeColumn(label)
    if (type === 'date' || id === 'createdAt' || isTimeColumn) {
      const rawValue = (row as any)[id] ?? (isTimeColumn ? row.createdAt : undefined)
      const parsedValue = rawValue ? moment(rawValue) : null
      const defaultFormat = isTimeColumn ? 'HH:mm' : 'DD/MM/YYYY'
      return (
        <Typography variant="body2">
          {parsedValue?.isValid() ? parsedValue.format(format || defaultFormat) : '—'}
        </Typography>
      )
    }
    if (id === 'examType') {
      return <Typography variant="body2">{getExamTypeLabel(row.examType)}</Typography>
    }
    if (id === 'state') {
      return <StatusPill label={getStateLabel(row.state)} color={getStateColor(row.state)} />
    }
    return <Typography variant="body2">{(row as any)[id] ?? '—'}</Typography>
  }
}

// Shared by buildColumns and getActiveColumnIds so both agree on which columns are
// actually in play — PSP-configured columnOptions when present, DEFAULT_COLUMNS otherwise.
function getActiveColumns(columnConfig?: EchoRequestColumnConfig[]): EchoRequestColumnConfig[] {
  return columnConfig && columnConfig.length > 0 ? columnConfig : DEFAULT_COLUMNS
}

/**
 * The ids of the columns actually shown (PSP-configured, or DEFAULT_COLUMNS as fallback).
 * Lets the page's "Filtri" drawer hide a filter whose corresponding column was turned off
 * in the PSP config, instead of offering a filter for data the table never displays.
 */
export function getActiveColumnIds(columnConfig?: EchoRequestColumnConfig[]): Set<string> {
  return new Set(getActiveColumns(columnConfig).map(col => col.id))
}

/**
 * Columns are PSP-driven (`echocardiographicRequestsConfigurations.columnOptions`),
 * falling back to a sensible default set — mirrors the pattern in biologicalSamplesColumns.tsx
 * / ExamsTab.tsx. `patient` renders as the mobile card header and `state` as its top-right
 * badge, same convention used elsewhere in pages-new.
 */
export function buildColumns(columnConfig?: EchoRequestColumnConfig[]): ColumnDef<EchoRequestRow>[] {
  const config = getActiveColumns(columnConfig)
  return config.map((col, index) => ({
    // `id` is not unique across columns (createdAt appears twice), so the key is
    // disambiguated with the index.
    key: `${col.id}-${index}`,
    header: col.label,
    mobileCardHeader: col.id === 'patient',
    mobileCardBadge: col.id === 'state',
    render: renderCell(col.id, col.type, col.label, col.format),
  }))
}